// ═══════════════════════════════════════════════════════════════════════════════
// Receipt list rows — same visual pattern as employer Task Management History tab
// (date sections YYYY-MM-DD + 今天/昨天, status icon, title, subtitle, badge, chevron)
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  RECEIPT_STATUS,
  type Language,
  type ReceiptListItem,
  type ReceiptStatus,
} from '@fdh/shared';
import { CornerStatusRibbon } from '../ui/CornerStatusRibbon';
import { LIST_NAV_ROW_CLASS } from '../ui/ListNavRow';
import { ListRowChevron } from '../ui/ListRowChevron';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import { receiptStatusRibbonClass } from '../../utils/listCornerStatusRibbon';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export const getTodayIsoDateInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

export const receiptListCompanyTitle = (row: ReceiptListItem): string => {
  const c = row.company.trim();
  return c !== '' ? c : '—';
};

const groupReceiptsByReceiptDate = (
  items: ReceiptListItem[],
): Array<{ date: string; rows: ReceiptListItem[] }> => {
  const byDate = new Map<string, ReceiptListItem[]>();
  items.forEach((row) => {
    const d = row.receiptDate;
    const list = byDate.get(d) ?? [];
    list.push(row);
    byDate.set(d, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  return dates.map((date) => ({
    date,
    rows: (byDate.get(date) ?? [])
      .slice()
      .sort((a, b) => {
        const cmp = (b.submittedAt || '').localeCompare(a.submittedAt || '');
        if (cmp !== 0) return cmp;
        return b.id.localeCompare(a.id);
      }),
  }));
};

const ReceiptStatusRowIcon = ({ status }: { status: ReceiptStatus }) => {
  if (status === RECEIPT_STATUS.SAVE) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/55 dark:text-amber-200"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
    );
  }

  if (status === RECEIPT_STATUS.SUBMITTED) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-900 dark:bg-blue-950/55 dark:text-blue-100"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/55 dark:text-emerald-400"
      aria-hidden
    >
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
};

export type ReceiptListHistoryStyleGroupedProps = {
  items: ReceiptListItem[];
  todayYmd: string;
  timezone: string;
  language: Language;
  t: TranslateFn;
  formatMoney: (n: number) => string;
  receiptDetailPath?: (receiptId: string) => string;
};

export const ReceiptListHistoryStyleGrouped = ({
  items,
  todayYmd,
  timezone,
  language,
  t,
  formatMoney,
  receiptDetailPath = (id) => `/receipts/${id}`,
}: ReceiptListHistoryStyleGroupedProps) => {
  const groups = useMemo(() => groupReceiptsByReceiptDate(items), [items]);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section
          key={group.date}
          className="space-y-2"
          aria-labelledby={`receipt-list-date-${group.date}`}
        >
          <h2
            id={`receipt-list-date-${group.date}`}
            className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
          >
            <span className="min-w-0">
              {buildEmployerGroupedTaskDateHeading({
                dateYmd: group.date,
                todayYmd,
                tomorrowYmd: null,
                timezone,
                language,
                t,
              })}
            </span>
            <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
              {t('task.history.groupTotalCount', { count: group.rows.length })}
            </span>
          </h2>
          <ul className="space-y-2" role="list">
            {group.rows.map((row) => {
              const title = receiptListCompanyTitle(row);
              const statusLabel =
                row.status === RECEIPT_STATUS.SAVE
                  ? t('receipt.status.save')
                  : row.status === RECEIPT_STATUS.SUBMITTED
                    ? t('receipt.status.submitted')
                    : t('receipt.status.reviewed');
              return (
                <li key={row.id}>
                  <Link
                    to={receiptDetailPath(row.id)}
                    tabIndex={0}
                    aria-label={`${title} — ${formatMoney(row.totalAmount)} — ${statusLabel}`}
                    className={LIST_NAV_ROW_CLASS}
                  >
                    <CornerStatusRibbon label={statusLabel} styleClass={receiptStatusRibbonClass(row.status)} />
                    <ReceiptStatusRowIcon status={row.status} />
                    <div className="min-w-0 flex-1 pr-10">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                      <p className="mt-1 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {formatMoney(row.totalAmount)}
                      </p>
                    </div>
                    <ListRowChevron />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
